import { useCallback, useEffect, useMemo } from "react";
import { useDrag } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import moment from "moment-timezone";
import { Lock } from "lucide-react";
import BreakOvertimeIndicator from "../../../../../../../components/BreakOvertimeIndicator";
import SectionInfo from "../../../../../../../components/SectionInfo";
import ShiftTooltip from "../../../../../../../components/tooltip/ShiftTooltip";
import { createMoreModalDragItem } from "../../DragLayer";
import { useMenuContext } from "../../../../../../../hooks/context/useMenuContext";
import { usePermissions } from "../../../../../../../hooks/usePermissions";
import { useCompanyTimeFormat } from "../../../../../../../../../../utils/hooks/company";
import { useShiftPreferenceStyle } from "../../../../../../../hooks/styles/useShiftPreferenceStyle";
import { getSectionDurations } from "../../../../../../../helpers/data/getSectionDurations";
import OpenRequestIndicator from "../../../../../../../components/OpenRequestIndicator";
import { renderUserName } from "../../../../../../../../../../utils/helpers/user";
import { cn } from "../../../../../../../../../../lib/utils";

function Item({ shift, close }: any) {
    const timeFormat = useCompanyTimeFormat();
    const { openMenu } = useMenuContext();
    const { canCreate, canEdit } = usePermissions();
    const preferenceStyle = useShiftPreferenceStyle(shift);

    const [{ isDragging }, dragRef, previewRef] = useDrag({
        type: "calendar-event",
        item: () => createMoreModalDragItem({ ...shift, user: shift?.user?.id || shift?.user }, shift) as any,
        canDrag: (canCreate || canEdit) && !shift?.locked,
        collect: (monitor) => ({ isDragging: !!monitor.isDragging() }),
    });

    const {
        overtimeDuration,
        breakDuration
    } = useMemo(() => getSectionDurations(shift?.sections), [shift?.sections]);

    const onContextMenu = useCallback((e: any, shift: any) => {
        e.preventDefault();
        e.stopPropagation();

        openMenu({
            id: "event-menu",
            x: e.clientX,
            y: e.clientY + 10,
            selected: shift,
            resource: shift?.user,
            moreModal: true
        });
    }, [openMenu]);

    useEffect(() => {
        previewRef(getEmptyImage(), { captureDraggingState: true });
    }, [previewRef]);

    useEffect(() => {
        if (isDragging) {
            close();
        }
    }, [isDragging, close]);

    return (
        <ShiftTooltip data={{ ...shift, resource: shift?.user }} side="top">
            <div
                key={shift.id}
                ref={dragRef as any}
                className={cn(
                    "tw-cursor-pointer tw-rounded-md tw-px-3 tw-py-2 tw-text-sm tw-mb-1 tw-bg-indigo-100 tw-border-l-2 tw-border-indigo-300 tw-flex tw-justify-between",
                    shift.status === "approved" && "tw-bg-green-100 tw-border-l-green-400",
                    shift?.locked && "tw-bg-violet-100 tw-border-l-violet-400",
                    shift?.status === "submitted" && "tw-bg-rose-100 tw-border-l-rose-400",
                )}
                style={preferenceStyle}
                onContextMenu={(e: any) => onContextMenu(e, shift)}
            >
                <div>
                    <div className="tw-mb-1 tw-font-semibold tw-text-primary tw-flex tw-items-center tw-gap-1">
                        {shift?.locked && (<Lock size={15} strokeWidth={3} />)}
                        <OpenRequestIndicator event={shift} />
                        <h1>{renderUserName(shift?.user)}</h1>
                    </div>
                    <div className="tw-flex tw-items-center tw-font-medium tw-mb-1">
                        <span>
                            {moment(shift?.startDate).format(timeFormat)}
                        </span>
                        <span className="tw-px-2">-</span>
                        <span>
                            {moment(shift?.endDate).format(timeFormat)}
                        </span>
                    </div>
                    <SectionInfo
                        section={shift?.sections?.[0]}
                        className="tw-pt-0.5"
                    />
                </div>

                <BreakOvertimeIndicator
                    breakDuration={breakDuration}
                    overtimeDuration={overtimeDuration}
                />
            </div>
        </ShiftTooltip>
    );
}

export default Item;
